import type { TableProps } from 'antd';
import { Button, Col, Form, Input, Modal, Row, Tabs } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useStoreActions, useStoreState } from '../../store';
import { CloudDownloadOutlined, FileSearchOutlined } from '@ant-design/icons';
import { CarrierServiceMethodDropDown } from '../../components/workflow/carrier/CarrierServiceMethodDropDown';
import { CountryCodeDropdown } from '../../components/shared/CountryCodeDropdown';
import type { TntJobV2, TntType } from '../../store/services/TimeInTransitService/types';
import { TntUpload, findExistingDataSetId } from '../../components/timeInTransit/TntUpload';
import { TNT_UPLOAD_TYPE_CONFIG } from './tntUploadTypeConfig';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { TntTable } from './TntTable';
import { TNT_PAGINATION_MODAL_KEY, TntPaginationModal } from './TntProdPaginationModal';
import { TntCarrierDefaultTable } from './TntCarrierDefaultTable';
import type { PaginatedTntData } from '../../store/timeInTransit/types';
import { useSession } from '~/hooks/useSession';

export enum TntTabType {
  PROD = 'production',
  PROMISE = 'promise',
  CARRIER_DEFAULT = 'carrier_default',
  SIMULATION = 'simulation',
}

const countryPriority = ['CA', 'US'];
// prioritize US and CA without changing the order
function countrySort(a: string, b: string) {
  const aPriority = countryPriority.indexOf(a) + 1;
  const bPriority = countryPriority.indexOf(b) + 1;

  return bPriority - aPriority;
}

export interface TntManagementContainerProps {
  showTabs?: boolean;
  showTitle?: boolean;
  showUpload?: boolean;
  removeColumns?: string[];
  simulationTableProps?: TableProps<TntJobV2>;
  productionTableProps?: TableProps<TntJobV2>;
}

const TntManagementContainer = ({
  showTabs = true,
  showTitle = true,
  showUpload = true,
  removeColumns = [],
  simulationTableProps,
  productionTableProps,
}: TntManagementContainerProps) => {
  const loading = useStoreState((state) => state.loading.loadingFlags.timeInTransit);
  const { isShipiumView } = useSession();
  const sessionPartnerId = useStoreState((state) => state.session.user?.sessionPartnerId);
  const timeInTransitSimulationTableData = useStoreState(
    (state) => state.timeInTransit.timeInTransitSimulationTableData,
  );
  const prodDataSetType = useStoreState((state) => state.timeInTransit.prodDataSetType);
  const fetchTntDataSetTypes = useStoreActions((actions) => actions.timeInTransit.fetchTntDataSetTypes);
  const fetchTntRecordsByVersion = useStoreActions((actions) => actions.timeInTransit.fetchTntRecordsByVersion);
  const downloadTntRecordsBySearch = useStoreActions((actions) => actions.timeInTransit.downloadTntRecordsBySearchV2);
  const fetchTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchTntJobsV2);
  const fetchSimulationTNTJobs = useStoreActions((actions) => actions.timeInTransit.fetchSimulationTntJobsV2);
  const fetchCarrierDefaultJobs = useStoreActions((actions) => actions.timeInTransit.fetchCarrierDefaultTntJobsV2);
  // Own thunk for the promise tab — writes into its own `promiseJobs` store slot so it can never
  // clobber (or be clobbered by) the production tab's fetchTntJobsV2 call. Mirrors
  // fetchCarrierDefaultJobs, the established pattern in this file for a tab-scoped job fetch.
  const fetchPromiseTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchPromiseTntJobsV2);
  const [form] = Form.useForm();
  const originPostalCode = Form.useWatch('originPostalCode', form);
  const destinationPostalCode = Form.useWatch('destinationPostalCode', form);
  const [query, setQuery] = useSearchParams();
  const timeInTransitState = useStoreState((state) => state.timeInTransit.timeInTransitState);
  const prodTntDataSetTypeId = findExistingDataSetId(
    timeInTransitState,
    TNT_UPLOAD_TYPE_CONFIG.production.tntSourceName,
  );
  // The promise tab's own dataset lookup — must resolve via its own source name
  // (csm-blend-partner-provided), never the production one. Reusing prodTntDataSetTypeId here would
  // silently display the carrier-selection dataset's jobs under the "Delivery Promise" heading.
  const promiseTntDataSetTypeId = findExistingDataSetId(
    timeInTransitState,
    TNT_UPLOAD_TYPE_CONFIG.promise.tntSourceName,
  );
  const prodTableDataWithName = useStoreState((state) => state.timeInTransit.tntProdTableDataWithName);
  // Sourced from the store's own `promiseJobs` slot (see TimeInTransitModel) rather than the shared
  // `tntJobs` slot the production tab writes to — that slot is single-writer-scoped per tntDataSetId
  // and would otherwise be clobbered by whichever of the two tabs' fetches resolves last.
  const promiseTableDataWithName = useStoreState((state) => state.timeInTransit.tntPromiseTableDataWithName);
  const tntCarrierDefaultTableDataWithName = useStoreState(
    (state) => state.timeInTransit.tntCarrierDefaultTableDataWithName,
  );
  const simulationTableDataWithName = useStoreState((state) => state.timeInTransit.tntSimulationTableDataWithName);
  const toggleModal = useStoreActions((action) => action.navigation.toggleModal);

  showTabs = showTabs && isShipiumView; // remove when we stop shipium gated Tnt features

  const [tntSearchLoading, setTntSearchLoading] = useState(false);
  const [carrierId, setCarrierId] = useState('');
  const [carrierSpecificData, setCarrierSpecificData] = useState<TntJobV2[]>([]);
  const [modal, contextHolder] = Modal.useModal();

  const parsedParams = useMemo(() => {
    let tab: TntType;
    const queryTab = query.get('tab');

    if (!showTabs) {
      tab = TntTabType.SIMULATION;
      if (queryTab) {
        query.delete('tab');
        setQuery(query);
      }
    } else {
      tab = (queryTab ?? TntTabType.SIMULATION) as TntType;
    }

    return {
      activeTab: tab,
    };
  }, [query, setQuery, showTabs]);

  const { activeTab } = parsedParams;

  const handleViewCarrierDefaultTable = () => {
    const carrierId = form.getFieldValue('carrierId');
    setCarrierId(carrierId);
    const carrierData = tntCarrierDefaultTableDataWithName.filter(
      (tntData: TntJobV2) => tntData.carrierId === carrierId,
    );
    setCarrierSpecificData(carrierData);
  };

  // Which tab's job/version history the View and Download controls should search against. Promise
  // must resolve to its own table data — falling through to carrierSpecificData (only ever populated
  // for the carrier_default tab) left both controls permanently erroring on the promise tab.
  const getCurrentTabData = () => {
    if (activeTab === TntTabType.PROMISE) return promiseTableDataWithName?.jobs;
    if (activeTab === TntTabType.PROD) return prodTableDataWithName?.jobs;
    return carrierSpecificData;
  };

  function handleSubmit() {
    setTntSearchLoading(true);
    const currentTabData = getCurrentTabData();
    const tntVersionHistory = currentTabData
      ?.filter((job: TntJobV2) => job.uploadJobStatus === 'COMPLETE')
      .sort((a: TntJobV2, b: TntJobV2) => (a?.versionNumber || 0) - (b?.versionNumber || 0));
    const activeDataSetVersion = tntVersionHistory?.[tntVersionHistory.length - 1]?.tntDataSetVersionId;
    if (!activeDataSetVersion) {
      modal.error({
        title: 'Failed to download',
        content: 'No active Data Set found.',
      });
      setTntSearchLoading(false);
      return;
    }
    const formVals = form.getFieldsValue();
    const filters = {
      dataSetId: tntVersionHistory?.[tntVersionHistory.length - 1]?.tntDataSetId,
      dataSetVersionId: activeDataSetVersion,
      carrierServiceMethodIds: formVals.serviceMethod,
      originLocations: formVals.originCountry
        ? [
            {
              countryCode: formVals.originCountry,
              postalCode: formVals.originPostalCode,
            },
          ]
        : [],
      destinationLocations: formVals.destinationCountry
        ? [
            {
              countryCode: formVals.destinationCountry,
              postalCode: formVals.destinationPostalCode,
            },
          ]
        : [],
    };
    downloadTntRecordsBySearch({
      name: tntVersionHistory?.[tntVersionHistory.length - 1]?.tntDataSetName ?? '',
      tntSearch: filters,
    }).finally(() => {
      setTntSearchLoading(false);
    });
  }

  const openModal = (records: PaginatedTntData[]) => {
    toggleModal({ open: true, key: TNT_PAGINATION_MODAL_KEY, params: records });
  };

  const handleViewFilteredData = async () => {
    const currentTabData = getCurrentTabData();
    const tntVersionHistory = currentTabData
      ?.filter((job: TntJobV2) => job.uploadJobStatus === 'COMPLETE')
      .sort((a: TntJobV2, b: TntJobV2) => (a?.versionNumber || 0) - (b?.versionNumber || 0));
    const activeDataSetVersion = tntVersionHistory?.[tntVersionHistory.length - 1]?.tntDataSetVersionId;
    if (!activeDataSetVersion) {
      modal.error({
        title: 'Failed to view',
        content: 'No active Data Set found.',
      });
      return;
    }
    const formVals = form.getFieldsValue();
    const filters = {
      tntDataSetVersionId: activeDataSetVersion,
      carrierServiceMethodIds: formVals.serviceMethod,
      originLocations: formVals.originCountry
        ? [
            {
              countryCode: formVals.originCountry,
              postalCode: formVals.originPostalCode,
            },
          ]
        : [],
      destinationLocations: formVals.destinationCountry
        ? [
            {
              countryCode: formVals.destinationCountry,
              postalCode: formVals.destinationPostalCode,
            },
          ]
        : [],
    };
    const records = await fetchTntRecordsByVersion(filters);
    openModal(records);
  };

  useEffect(() => {
    fetchTntDataSetTypes({});
  }, [fetchTntDataSetTypes]);

  useEffect(() => {
    fetchCarrierDefaultJobs({});
  }, [fetchCarrierDefaultJobs]);

  useEffect(() => {
    if (prodTntDataSetTypeId) fetchTntJobs(prodTntDataSetTypeId);
  }, [fetchTntJobs, prodTntDataSetTypeId]);

  useEffect(() => {
    if (promiseTntDataSetTypeId) fetchPromiseTntJobs(promiseTntDataSetTypeId);
  }, [fetchPromiseTntJobs, promiseTntDataSetTypeId]);

  useEffect(() => {
    fetchSimulationTNTJobs();
  }, [fetchSimulationTNTJobs]);

  useEffect(() => {
    if (timeInTransitSimulationTableData.length) {
      form.validateFields(['download']);
    }
  }, [prodDataSetType, form, timeInTransitSimulationTableData]);
  return (
    <>
      {showTabs && (
        <Tabs
          onTabClick={(val) => {
            query.set('tab', val);
            setQuery(query);
          }}
          type={'card'}
          activeKey={activeTab}
          items={[
            { key: 'production', label: 'CS Partner TNT Overrides' },
            { key: 'promise', label: 'Promise Partner TNT Overrides' },
            { key: 'carrier_default', label: 'Global Carrier Defaults' },
            { key: 'simulation', label: 'Simulation' },
          ]}
        />
      )}
      {showTabs && activeTab === TntTabType.CARRIER_DEFAULT && (
        <Form form={form}>
          <h3>Carrier Default TNTs</h3>
          <Row wrap={false}>
            <Form.Item name="carrierId" label={<b>Carrier Select</b>}>
              <CarrierServiceMethodDropDown hideServiceMethods={true} singleSelect />
            </Form.Item>
            <Button onClick={handleViewCarrierDefaultTable} type="primary" style={{ marginLeft: 10 }}>
              View
            </Button>
          </Row>
        </Form>
      )}
      {showTabs &&
        (activeTab === TntTabType.PROD ||
          activeTab === TntTabType.PROMISE ||
          (activeTab === TntTabType.CARRIER_DEFAULT && carrierId)) && (
          <Form form={form} onFinish={handleSubmit}>
            {showTitle && activeTab === TntTabType.PROD && <h3>Production TNT Overrides</h3>}
            {showTitle && activeTab === TntTabType.PROMISE && <h3>Delivery Promise TNT Overrides</h3>}
            <Row justify="start" wrap={false}>
              {activeTab !== TntTabType.PROMISE && (
                <Col xs={8} xl={4} style={{ paddingRight: '20px' }}>
                  <h4 style={{ whiteSpace: 'nowrap' }}>Carrier Service Method</h4>
                  <Form.Item name="serviceMethod">
                    <CarrierServiceMethodDropDown
                      allowedCarriers={activeTab === TntTabType.CARRIER_DEFAULT ? [carrierId] : undefined}
                      hideCarriers={true}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={8} xl={4} style={{ paddingRight: '20px' }}>
                <h4 style={{ whiteSpace: 'nowrap' }}>Origin Country</h4>
                <Form.Item
                  name="originCountry"
                  dependencies={['originPostalCode']}
                  rules={[{ required: !!originPostalCode, message: 'Country required when searching by postal code.' }]}
                >
                  <CountryCodeDropdown allowClear={true} customSort={countrySort} />
                </Form.Item>
              </Col>
              <Col xs={8} xl={4} style={{ paddingRight: '20px' }}>
                <h4 style={{ whiteSpace: 'nowrap' }}>Origin Postal Code</h4>
                <Form.Item
                  name="originPostalCode"
                  rules={[{ min: 3, max: 10, message: 'Postal Code must be between 3 and 10 characters.' }]}
                >
                  <Input placeholder="Origin Postal Code" />
                </Form.Item>
              </Col>
              <Col xs={8} xl={4} style={{ paddingRight: '20px' }}>
                <h4 style={{ whiteSpace: 'nowrap' }}>Destination Country</h4>
                <Form.Item
                  name="destinationCountry"
                  dependencies={['destinationPostalCode']}
                  rules={[
                    { required: !!destinationPostalCode, message: 'Country required when searching by postal code.' },
                  ]}
                >
                  <CountryCodeDropdown allowClear={true} customSort={countrySort} />
                </Form.Item>
              </Col>
              <Col xs={8} xl={4} style={{ paddingRight: '20px' }}>
                <h4 style={{ whiteSpace: 'nowrap' }}>Destination Postal Code</h4>
                <Form.Item
                  name="destinationPostalCode"
                  rules={[{ min: 3, max: 10, message: 'Postal Code must be between 3 and 10 characters.' }]}
                >
                  <Input placeholder="Destination Postal Code" />
                </Form.Item>
              </Col>
              <Col style={{ marginTop: '30px', paddingRight: '20px' }}>
                <Form.Item
                  name="view"
                  rules={[
                    {
                      validator: () => {
                        if (!prodDataSetType?.id && activeTab === TntTabType.PROD) {
                          return Promise.reject(new Error('No active Production TNT Overrides.'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Button
                    title="View"
                    disabled={
                      (!prodDataSetType?.id && activeTab === TntTabType.PROD) ||
                      (!promiseTntDataSetTypeId && activeTab === TntTabType.PROMISE) ||
                      (!carrierSpecificData && activeTab === TntTabType.CARRIER_DEFAULT)
                    }
                    //loading={tntSearchLoading}
                    onClick={() => handleViewFilteredData()}
                  >
                    View <FileSearchOutlined />
                  </Button>
                </Form.Item>
              </Col>
              <Col style={{ marginTop: '30px' }}>
                <Form.Item
                  name="download"
                  rules={[
                    {
                      validator: () => {
                        if (!prodDataSetType?.id && activeTab === TntTabType.PROD) {
                          return Promise.reject(new Error('No active Production TNT Overrides.'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Button
                    title="Download"
                    disabled={
                      (!prodDataSetType?.id && activeTab === TntTabType.PROD) ||
                      (!promiseTntDataSetTypeId && activeTab === TntTabType.PROMISE)
                    }
                    loading={tntSearchLoading}
                    onClick={form.submit}
                  >
                    Download <CloudDownloadOutlined />
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      {showTabs && activeTab === TntTabType.SIMULATION && showTitle && <h3>Simulation TNTs</h3>}

      {showUpload &&
        ((activeTab === TntTabType.CARRIER_DEFAULT && carrierId) ||
          activeTab === TntTabType.SIMULATION ||
          activeTab === TntTabType.PROD ||
          activeTab === TntTabType.PROMISE) && (
          <div style={{ marginBottom: '20px' }}>
            {/*
              Keyed by tab so each tab gets its own TntUpload instance. Without it React reconciles
              by position and one instance serves every tab, carrying its state across switches —
              the in-flight spinner and outcome modal would surface on whichever tab is open when
              the job settles, attributing the upload to the wrong dataset.
            */}
            <TntUpload key={activeTab} type={activeTab} carrierId={carrierId} />
          </div>
        )}
      <>
        {activeTab === TntTabType.PROD && (
          <>
            <TntTable
              loading={loading}
              data={prodTableDataWithName.jobs}
              tableProps={productionTableProps}
              removeColumns={removeColumns}
              tntSourceName={TNT_UPLOAD_TYPE_CONFIG.production.tntSourceName}
            />
            <TntPaginationModal />
          </>
        )}
        {activeTab === TntTabType.PROMISE && (
          <>
            <TntTable
              loading={loading}
              data={promiseTableDataWithName.jobs}
              removeColumns={removeColumns}
              tntSourceName={TNT_UPLOAD_TYPE_CONFIG.promise.tntSourceName}
            />
            <TntPaginationModal />
          </>
        )}
        {activeTab === TntTabType.CARRIER_DEFAULT && carrierId && (
          <>
            <TntCarrierDefaultTable
              loading={loading}
              data={carrierSpecificData}
              tableProps={productionTableProps}
              removeColumns={removeColumns}
              carrierId={carrierId}
            />
            <TntPaginationModal />
          </>
        )}
        {activeTab === TntTabType.SIMULATION && (
          <TntTable
            loading={loading}
            data={simulationTableDataWithName(sessionPartnerId ?? '')}
            tableProps={simulationTableProps}
            removeColumns={[...removeColumns, 'version']}
            rowKeyOverride={'tntDataSetId'}
          />
        )}
      </>
      {contextHolder}
    </>
  );
};

export default TntManagementContainer;
